/**
 * SOFSEK — Delegation Planner & Live Monitoring Console (§4). Additive pool —
 * locked decision: while active, the backup subject's evaluated permissions
 * are their OWN duties PLUS the absent subject's, not a swap (implemented in
 * sfk_v_subject_permission_flat, Phase 5 migration). State (SCHEDULED/ACTIVE/
 * EXPIRED) is derived from the window timestamps, never stored. A predictive
 * check runs before scheduling; Force-Revoke stamps revoked_at.
 */
import { Fragment, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiDb } from '../../lib/api_client';
import { routedInsert, routedUpdate } from '../../lib/routed_writes';
import { hasAccess, type AuthSession } from '../../types/auth';
import { Modal } from '../../components/common/Modal';
import { EmptyState } from '../../components/common/EmptyState';
import { RelatedChildrenPanel, RelatedExpandButton, useExpandedRows, type RelatedChildGroup } from '../../components/common/RelatedChildrenPanel';
import { FacetFilter } from '../../components/common/FacetFilter';
import { ClearFiltersButton } from '../../components/common/ClearFiltersButton';
import { useFormatDateTime } from '../../lib/format_date';
import { PageHeader } from '../../components/common/PageHeader';
import { StatStrip } from '../../components/common/StatStrip';
import { ContentCard } from '../../components/common/ContentCard';
import { Plus, RefreshCw, Search } from 'lucide-react';
import { PersonalizationProvider, PersonalizationToggle, PersonalizedGrid, PersonalizedField, GRID_TABLE_CLASS } from '../../components/personalization';
import type { GridColumnDef } from '../../components/personalization';

const PAGE_CODE = 'SFK.DELEGATION.LIST';
const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-sm';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface CodeOpt { code: string; name: string; }
interface DelegationRow {
  delegation_id: number;
  source_app_user_id: number;
  target_app_user_id: number;
  start_timestamp: string;
  end_timestamp: string;
  revoked_at: string | null;
}

function derivedState(row: DelegationRow): 'SCHEDULED' | 'ACTIVE' | 'EXPIRED' | 'REVOKED' {
  if (row.revoked_at) return 'REVOKED';
  const now = Date.now();
  const start = new Date(row.start_timestamp).getTime();
  const end = new Date(row.end_timestamp).getTime();
  if (now < start) return 'SCHEDULED';
  if (now > end) return 'EXPIRED';
  return 'ACTIVE';
}

const STATE_LABEL: Record<string, string> = { SCHEDULED: 'Scheduled', ACTIVE: 'Active', EXPIRED: 'Expired', REVOKED: 'Revoked' };
const STATE_CLASS: Record<string, string> = {
  SCHEDULED: 'bg-csm-warning-soft text-csm-warning',
  ACTIVE: 'bg-csm-success-soft text-csm-success',
  EXPIRED: 'bg-csm-surface-alt text-csm-watermark',
  REVOKED: 'bg-csm-danger-soft text-csm-danger',
};

const GRID_COLS: GridColumnDef[] = [
  { key: 'source', header: 'Absent App User', defaultWidth: 200 },
  { key: 'target', header: 'Backup App User', defaultWidth: 200 },
  { key: 'window', header: 'Window', defaultWidth: 280 },
  { key: 'state', header: 'State', defaultWidth: 120 },
];

function DelegationInner({ session }: { session: AuthSession }) {
  const { t } = useTranslation();
  const formatDateTime = useFormatDateTime();
  const [rows, setRows] = useState<DelegationRow[]>([]);
  const [subjects, setSubjects] = useState<CodeOpt[]>([]);
  const [filter, setFilter] = useState('');
  const [stateFacet, setStateFacet] = useState<string[]>(['SCHEDULED', 'ACTIVE']);
  const [creating, setCreating] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reload = async () => {
    setBusy(true); setError(null);
    try {
      const [delRes, subjRes] = await Promise.all([
        apiDb.from('sfk_delegation').select('delegation_id, source_app_user_id, target_app_user_id, start_timestamp, end_timestamp, revoked_at').order('start_timestamp', { ascending: false }),
        apiDb.from('csm_user_master').select('user_id, display_name'),
      ]);
      if (delRes.error) throw delRes.error;
      if (subjRes.error) throw subjRes.error;
      setRows((delRes.data ?? []) as unknown as DelegationRow[]);
      setSubjects(((subjRes.data ?? []) as { user_id: number; display_name: string }[]).map(s => ({ code: String(s.user_id), name: s.display_name })));
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

   
  useEffect(() => {
    void reload();
    // Coarse refresh (not per-second) so SCHEDULED/ACTIVE/EXPIRED derived state stays current.
    const id = setInterval(() => void reload(), 30000);
    return () => clearInterval(id);
  }, []);

  const subjectName = (id: number) => subjects.find(s => s.code === String(id))?.name ?? String(id);

  const detail = useExpandedRows<number>();
  // §6/§17: csm_role_page_access decides which write affordances this user is
  // offered. The server refuses the write regardless (RLS, via the Common
  // Routine) — this stops the page offering an action that can only error.
  const canAdd    = hasAccess(session, PAGE_CODE, 'add');
  const canChange = hasAccess(session, PAGE_CODE, 'change');
  // §17.B13 — a delegation's related parties: the absent and backup subjects.
  const buildRelatedGroups = (r: DelegationRow): RelatedChildGroup[] => [
    { key: 'parties', title: t('sfk.delegation.related.parties', 'Parties'), items: [
      { id: 'source', label: `${t('sfk.delegation.col.absent', 'Absent App User')}: ${subjectName(r.source_app_user_id)}`, title: String(r.source_app_user_id) },
      { id: 'target', label: `${t('sfk.delegation.col.backup', 'Backup App User')}: ${subjectName(r.target_app_user_id)}`, title: String(r.target_app_user_id) },
    ] },
  ];

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return rows.filter(r =>
      (stateFacet.length === 0 || stateFacet.includes(derivedState(r))) &&
      (!q || subjectName(r.source_app_user_id).toLowerCase().includes(q) || subjectName(r.target_app_user_id).toLowerCase().includes(q))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, stateFacet, filter, subjects]);

  const forceRevoke = async (row: DelegationRow) => {
    setBusy(true); setError(null);
    try {
      const { error: e } = await routedUpdate(session, 'sfk_delegation', 'delegation_id', row.delegation_id, { revoked_at: new Date().toISOString() });
      if (e) throw new Error(e.message);
      await reload();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <div className="flex h-full flex-col">
      <div className="flex min-h-0 flex-1 flex-col gap-[18px] overflow-auto p-6">
        <PageHeader
          title={t('sfk.delegation.title', 'Delegation Planner')}
          description={t('sfk.delegation.description', 'Cover arrangements — a backup app user temporarily holds an absent user\'s duties (additive pool).')}
          actions={<>
            <button type="button" onClick={() => void reload()} className="btn-secondary flex items-center gap-1.5"><RefreshCw className="h-3.5 w-3.5" /> {t('common.refresh', 'Refresh')}</button>
            {canAdd && (<button type="button" onClick={() => setCreating(true)} className="btn-primary flex items-center gap-1.5"><Plus className="h-3.5 w-3.5" /> {t('sfk.delegation.new', 'New Delegation')}</button>)}
          </>}
        />
        <StatStrip stats={[
          { value: rows.length, label: t('sfk.delegation.stat.total', 'Delegations') },
          { value: filtered.length, label: t('sfk.delegation.stat.shown', 'Shown') },
        ]} />
        {error && <div className="rounded-csm-card border border-csm-danger-border bg-csm-danger-soft px-3 py-1.5 text-csm-xs text-csm-danger">{error}</div>}
        <ContentCard className="flex min-h-0 flex-1 flex-col">
          <div className="flex shrink-0 flex-wrap items-center gap-2 border-b border-csm-line bg-white px-3 py-2">
            <div className="relative">
              <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-csm-ink4" aria-hidden />
              <input type="text" value={filter} onChange={e => setFilter(e.target.value)} placeholder={t('common.filter', 'Filter…')}
                className="h-8 w-72 rounded-csm-control border border-csm-border pl-8 pr-2 text-csm-md focus:border-primary focus:outline-none" />
            </div>
            <FacetFilter label={t('sfk.delegation.facet.state', 'State')} options={Object.entries(STATE_LABEL).map(([value, label]) => ({ value, label }))} selected={stateFacet} onChange={setStateFacet} />
            <ClearFiltersButton active={filter.trim() !== '' || stateFacet.length !== 2} onClear={() => { setFilter(''); setStateFacet([]); }} />
            <div className="ml-auto flex items-center gap-2">
              {busy && <span className="text-csm-xs text-csm-muted">{t('common.loading', 'Loading…')}</span>}
              <PersonalizationToggle />
            </div>
          </div>
          <div className="flex-1 overflow-auto">
        <PersonalizedGrid componentKey="grid" columns={GRID_COLS}>
          {(visibleCols, widths) => (
            <table className={GRID_TABLE_CLASS}>
              <thead className="sticky top-0 z-10 bg-csm-surface-alt text-csm-xs uppercase tracking-wider text-csm-ink-soft">
                <tr>
                  <th className="border-b border-csm-border px-1 py-1.5 text-left" style={{ width: '1%' }} aria-hidden>&nbsp;</th>
                  {visibleCols.map(col => (
                    <th key={col.key} className="border-b border-csm-border px-3 py-1.5 text-left" style={widths[col.key] ? { width: widths[col.key] } : undefined}>{col.header}</th>
                  ))}
                  <th className="border-b border-csm-border px-3 py-1.5 text-left" style={{ width: '1%' }}>{t('common.actions', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(r => {
                  const state = derivedState(r);
                  return (
                    <Fragment key={r.delegation_id}>
                    <tr className="hover:bg-csm-surface-alt">
                      <td className="border-b border-csm-border-soft px-1 py-1.5 text-center align-middle">
                        <RelatedExpandButton open={detail.isOpen(r.delegation_id)} hasChildren onToggle={() => detail.toggle(r.delegation_id)}
                          expandLabel={t('common.related.expand', 'Show related records')} collapseLabel={t('common.related.collapse', 'Hide related')} tooltip={t('common.related.tooltip', 'Show related records')} />
                      </td>
                      {visibleCols.map(col => (
                        <td key={col.key} className="border-b border-csm-border-soft px-3 py-1.5" style={widths[col.key] ? { width: widths[col.key] } : undefined}>
                          {col.key === 'source' && subjectName(r.source_app_user_id)}
                          {col.key === 'target' && subjectName(r.target_app_user_id)}
                          {col.key === 'window' && `${formatDateTime(r.start_timestamp)} → ${formatDateTime(r.end_timestamp)}`}
                          {col.key === 'state' && <span className={`rounded px-1.5 py-0.5 text-csm-xs ${STATE_CLASS[state]}`}>{t(`sfk.delegation.state.${state}`, STATE_LABEL[state])}</span>}
                        </td>
                      ))}
                      <td className="border-b border-csm-border-soft px-3 py-1.5 text-right">
                        {(state === 'ACTIVE' || state === 'SCHEDULED') && canChange && (
                          <button onClick={() => void forceRevoke(r)} className="rounded border border-csm-danger bg-white px-2 py-0.5 text-csm-xs text-csm-danger hover:bg-csm-danger-soft">{t('sfk.delegation.force_revoke', 'Force-Revoke')}</button>
                        )}
                      </td>
                    </tr>
                    {detail.isOpen(r.delegation_id) && (
                      <tr>
                        <td colSpan={visibleCols.length + 2} className="border-b border-csm-border-soft bg-csm-surface-alt/40 px-3 py-2">
                          <RelatedChildrenPanel groups={buildRelatedGroups(r)} emptyLabel={t('common.related.none', 'No related records')} />
                        </td>
                      </tr>
                    )}
                    </Fragment>
                  );
                })}
                {filtered.length === 0 && !busy && (
                  <tr><td colSpan={visibleCols.length + 2} className="py-12 text-center"><EmptyState title={t('common.no_records', 'No records')} /></td></tr>
                )}
              </tbody>
            </table>
          )}
        </PersonalizedGrid>
          </div>
        </ContentCard>
      </div>

      {creating && (
        <DelegationModal session={session} subjects={subjects}
          onClose={() => setCreating(false)}
          onSaved={() => { setCreating(false); void reload(); }} />
      )}
    </div>
  );
}

function DelegationModal({ session, subjects, onClose, onSaved }: { session: AuthSession; subjects: CodeOpt[]; onClose: () => void; onSaved: () => void }) {
  const { t } = useTranslation();
  const [sourceId, setSourceId] = useState('');
  const [targetId, setTargetId] = useState('');
  const [startAt, setStartAt] = useState('');
  const [endAt, setEndAt] = useState('');
  const [conflicts, setConflicts] = useState<number[] | null>(null);
  const [checkingConflicts, setCheckingConflicts] = useState(false);
  const [acknowledged, setAcknowledged] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const checkConflicts = async () => {
    if (!sourceId || !targetId) return;
    setCheckingConflicts(true); setError(null); setConflicts(null); setAcknowledged(false);
    try {
      const { data, error: e } = await apiDb.rpc('sfk_check_delegation_conflicts', { p_source_app_user_id: Number(sourceId), p_target_app_user_id: Number(targetId) });
      if (e) throw e;
      setConflicts(((data ?? []) as { rule_id: number }[]).map(r => r.rule_id));
    } catch (e) { setError((e as Error).message); } finally { setCheckingConflicts(false); }
  };

  const save = async () => {
    setBusy(true); setError(null);
    try {
      if (!sourceId || !targetId) throw new Error(t('sfk.delegation.err.app_users_required', 'Both app users are required.'));
      if (sourceId === targetId) throw new Error(t('sfk.delegation.err.same_app_user', 'Absent and backup app users must differ.'));
      if (!startAt || !endAt) throw new Error(t('sfk.delegation.err.window_required', 'Start and end are required.'));
      if (conflicts === null) throw new Error(t('sfk.delegation.err.check_required', 'Run the conflict check first.'));
      if (conflicts.length > 0 && !acknowledged) throw new Error(t('sfk.delegation.err.ack_required', 'Acknowledge the predicted conflicts before scheduling.'));

      const { error: e } = await routedInsert(session, 'sfk_delegation', {
        source_app_user_id: Number(sourceId),
        target_app_user_id: Number(targetId),
        start_timestamp: new Date(startAt).toISOString(),
        end_timestamp: new Date(endAt).toISOString(),
      });
      if (e) throw new Error(e.message);
      onSaved();
    } catch (e) { setError((e as Error).message); } finally { setBusy(false); }
  };

  return (
    <Modal
      title={t('sfk.delegation.new', 'New Delegation')}
      onClose={onClose}
      width="md"
      headerAction={<PersonalizationToggle />}
      footer={
        <>
          <button onClick={onClose} className="rounded border border-csm-border bg-white px-3 py-1 text-csm-sm hover:bg-csm-border-soft">{t('common.cancel', 'Cancel')}</button>
          <button onClick={() => void save()} disabled={busy} className="rounded border border-primary bg-primary px-3 py-1 text-csm-sm text-white hover:bg-primary-hover disabled:opacity-60">{busy ? t('common.saving', 'Saving…') : t('common.save', 'Save')}</button>
        </>
      }
    >
      <div className="grid grid-cols-2 gap-x-3">
        <PersonalizedField componentKey="modal" fieldKey="source_app_user_id" label={t('sfk.delegation.absent', 'Absent App User')}>
          <label className={labelCls}>{t('sfk.delegation.absent', 'Absent App User')}</label>
          <select value={sourceId} onChange={e => { setSourceId(e.target.value); setConflicts(null); }} className={`${inputCls} bg-white`}>
            <option value="">{t('common.select', '— select —')}</option>
            {subjects.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </PersonalizedField>
        <PersonalizedField componentKey="modal" fieldKey="target_app_user_id" label={t('sfk.delegation.backup', 'Backup App User')}>
          <label className={labelCls}>{t('sfk.delegation.backup', 'Backup App User')}</label>
          <select value={targetId} onChange={e => { setTargetId(e.target.value); setConflicts(null); }} className={`${inputCls} bg-white`}>
            <option value="">{t('common.select', '— select —')}</option>
            {subjects.map(s => <option key={s.code} value={s.code}>{s.name}</option>)}
          </select>
        </PersonalizedField>
      </div>
      <div className="grid grid-cols-2 gap-x-3">
        <PersonalizedField componentKey="modal" fieldKey="start_timestamp" label={t('sfk.delegation.start', 'Start')}>
          <label className={labelCls}>{t('sfk.delegation.start', 'Start')}</label>
          <input type="datetime-local" value={startAt} onChange={e => setStartAt(e.target.value)} className={inputCls} />
        </PersonalizedField>
        <PersonalizedField componentKey="modal" fieldKey="end_timestamp" label={t('sfk.delegation.end', 'End')}>
          <label className={labelCls}>{t('sfk.delegation.end', 'End')}</label>
          <input type="datetime-local" value={endAt} onChange={e => setEndAt(e.target.value)} className={inputCls} />
        </PersonalizedField>
      </div>

      <button onClick={() => void checkConflicts()} disabled={!sourceId || !targetId || checkingConflicts} className="mb-3 h-7 rounded border border-csm-border bg-white px-2 text-csm-sm hover:bg-csm-surface-alt">
        {checkingConflicts ? t('sfk.delegation.checking', 'Checking…') : t('sfk.delegation.check_conflicts', 'Check for Predicted Conflicts')}
      </button>

      {conflicts !== null && (
        <div className={`mb-3 rounded border px-2 py-1.5 text-csm-xs ${conflicts.length > 0 ? 'border-csm-warning-border bg-csm-warning-soft text-csm-warning' : 'border-csm-success-border bg-csm-success-soft text-csm-success'}`}>
          {conflicts.length === 0
            ? t('sfk.delegation.no_conflicts', 'No conflicts predicted for the combined permission pool.')
            : t('sfk.delegation.conflicts_found', `${conflicts.length} rule(s) would conflict once this delegation is active — a mitigating control tie-in is required to proceed.`)}
          {conflicts.length > 0 && (
            <label className="mt-2 flex items-center gap-2">
              <input type="checkbox" checked={acknowledged} onChange={e => setAcknowledged(e.target.checked)} className="h-4 w-4 accent-gray-700" />
              {t('sfk.delegation.acknowledge', 'I acknowledge these predicted conflicts and will mitigate via the Violations Tracker once active.')}
            </label>
          )}
        </div>
      )}

      {error && <div className="mt-3 rounded border border-csm-danger-border bg-csm-danger-soft px-2 py-1 text-csm-sm text-csm-danger">{error}</div>}
    </Modal>
  );
}

export function SfkDelegationPage({ session }: { session: AuthSession }) {
  return (
    <PersonalizationProvider pageCode={PAGE_CODE} session={session}>
      <DelegationInner session={session} />
    </PersonalizationProvider>
  );
}
